import { useMemo, useState } from 'react';
import {
  TextInput,
  useAuthForgotPasswordApi,
  GeneralLoginPage,
  LoginFormTitle,
  LoginColumnWrapper,
  PrimaryButton,
  LoginLinkStyled,
  Flex,
  email as emailValidator,
  LoginFormTitleIcon,
} from '@equifinance/shared';
import styled from 'styled-components';

const BackToLoginLink = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[1]};
  font-size: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
  align-items: center;
`}`;

const ForgotPasswordSentContent = styled.p<{ bold?: boolean }>`${({ theme, bold = false }) => `
  font-size: ${theme.fontSize[4]};
  font-weight: ${bold ? theme.fontWeight.bold : theme.fontWeight.normal};
  color: ${theme.colours.neutral[2]};
  line-height: ${theme.padding[7]};
`}`;

const EmailText = styled.span` ${({ theme }) => `
  text-decoration: underline;
  margin: 0 ${theme.padding[1]};
`}`;

const Icon = styled.i.attrs(() => ({
  className: 'mi-email',
}))`${({ theme }) => `
  font-size: ${theme.taskEvidenceActionsWidth};
  color: ${theme.colours.primary[1]};
`}`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { post } = useAuthForgotPasswordApi();

  const isValidEmail = useMemo(() => !!email && emailValidator.validator(email), [email]);

  const onClickResetPassword = () => {
    if (!isValidEmail) return;
    post(email)
      .finally(() => {
        setIsSubmitted(true);
      });
  };

  if (isSubmitted) {
    return (
      <GeneralLoginPage>
        <LoginFormTitleIcon>
          <Icon />
        </LoginFormTitleIcon>
        <LoginFormTitle>Check your email</LoginFormTitle>
        <ForgotPasswordSentContent bold>
          We have sent a message to
          <EmailText>{email}</EmailText>
          with a link to activate your account.
        </ForgotPasswordSentContent>
        <ForgotPasswordSentContent>
          {'If you don\'t see an email from us within a few minutes, please check your spam folder.'}
        </ForgotPasswordSentContent>
        <BackToLoginLink>
          Return to
          <LoginLinkStyled to="/login">Login</LoginLinkStyled>
        </BackToLoginLink>
      </GeneralLoginPage>
    );
  }

  return (
    <GeneralLoginPage>
      <LoginFormTitle>Forgot your password?</LoginFormTitle>
      <TextInput
        id="email"
        label="Enter email used to set up your account"
        placeholder="Email Address"
        value={email}
        onChange={setEmail}
        required
      />
      <LoginColumnWrapper>
        <PrimaryButton disabled={!isValidEmail} onClick={onClickResetPassword}>
          Send password reset link
        </PrimaryButton>
      </LoginColumnWrapper>
      <BackToLoginLink>
        Return to
        <LoginLinkStyled to="/login">Login</LoginLinkStyled>
      </BackToLoginLink>
    </GeneralLoginPage>
  );
}
